import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatSliderModule} from '@angular/material/slider';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {ChallengeBuilderProject} from 'src/app/core/model/project';
import {StartChallengeAction} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {CustomHeaderComponent} from 'src/app/projects/dialogs/builder-calendar/builder-calendar.component';
import {MessageService} from 'src/app/services/message.service';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';

interface DialogData {
  cbProject: ChallengeBuilderProject;
}

@Component({
  selector: 'cb-launch-challenge-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatDatepickerModule,
    MatMenuModule,
    MatSliderModule,
    OphButtonModule,
    OphXButtonComponent,
  ],
  templateUrl: './cb-launch-challenge-dialog.component.html',
  styleUrl: './cb-launch-challenge-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbLaunchChallengeDialogComponent {
  private store$ = inject(Store);
  private timezoneService = inject(TimezoneService);
  readonly dialogRef = inject(MatDialogRef<CbLaunchChallengeDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  readonly messageService = inject(MessageService);
  readonly customHeader = CustomHeaderComponent;

  label = signal(this.getInitialLabel());
  loading = signal(false);
  startDateTime = signal<moment.Moment>(moment().tz(this.timezoneService.getTimeZone()).startOf('day'));
  cycleSuccessScore = signal<number>(this.data.cbProject?.winConditions?.cycleSuccessScore || 80);
  levelProgressThreshold = signal<number>(8);

  cycleLength = this.data.cbProject?.challengeBuilder.cycleLength || 7;
  levelProgressThresholdOptions = Array.from({length: 100}, (_, i) => i + 1);
  minDate = moment().tz(this.timezoneService.getTimeZone()).startOf('day');

  readonly startDateDisplay = computed(() => this.startDateTime().format('dddd, MMM D'));
  readonly sliderValueDisplay = computed(() => `${this.cycleSuccessScore()}%`);
  readonly cycleLengthDisplay = computed(() => `${this.cycleLength} days`);

  private getInitialLabel(): string {
    const goals = this.data.cbProject?.challengeBuilder?.goals;
    const selected = goals?.find(g => g.selected);
    return this.data.cbProject?.label || selected?.name || '';
  }

  onStartDateChange(event: {value: moment.Moment}): void {
    if (event.value) {
      this.startDateTime.set(moment(event.value).tz(this.timezoneService.getTimeZone()));
    }
  }

  onSliderChange(value: number): void {
    this.cycleSuccessScore.set(value);
  }

  onLevelProgressThresholdChange(value: number): void {
    this.levelProgressThreshold.set(value);
  }

  onBack(): void {
    this.dialogRef.close();
  }

  onStartChallenge(): void {
    const formData = {
      label: this.label(),
      startDateTime: this.startDateTime().toISOString(),
      cycleSuccessScore: this.cycleSuccessScore(),
      levelProgressThreshold: this.levelProgressThreshold(),
    };

    const projectId = this.data.cbProject?._id;
    if (projectId) {
      this.loading.set(true);
      this.store$.dispatch(
        new StartChallengeAction({
          projectId,
          launchData: formData,
          onSuccess: () => this.onStartChallengeSuccess(),
          onFailure: (error: HttpErrorResponse) => this.onStartChallengeFailure(error),
        })
      );
    }
  }

  onStartChallengeSuccess(): void {
    this.loading.set(false);
    this.dialogRef.close();
  }

  onStartChallengeFailure(error: HttpErrorResponse): void {
    this.loading.set(false);
    this.messageService.add(error.error || 'Failed to start challenge', 'error');
  }
}
